/**
  * @class mmodNotifications
  * @description
  * Notifications module is used by other modules and services to inform
  * logged user on some significant data updates - e.g. incoming chat message,
  * update on incident or instruction etc.
  * Interface is practically the same on mPatrol and mOffice.
  * Notifications module displays notifications in order of their priority.
  * When multiple notifications are active, module collapses them by default,
  * allowing user to expand the list by arrow button.
  * Module handles three messages:
  * - mmNotify - base message that other listener sends to Notification module to display the notification
  * - mmNotifyClose - could be used to clear all notifications (e.g. when user logs out)
  * - mmNotifyActionClicked - used internally to handle click on notification buttons
  * Listeners can define not only notification text and priority, but also
  * define action buttons to provide shortcut to do something relevant to notification.
  * When user clicks such button, mmodNotifications will publish mmNotifyAction message
  * to the creator of notification. This is e.g. handled in mmodWorkflow so that it
  * activates the module and calls its mmOpen message to select particular item.
  */
import mmodModule from 'mmodModule'

export default class mmodNotifications extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'float', order: 2, x: 0, y: 0, width: '100%', height: '70%' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      open: {
        type: 'boolean',
        description: 'true to show all notifications, false to show only top one',
        default: false,
      },
      audio_disabled: {
        type: 'boolean',
        default: false,
        description: 'disable all audio sounds',
      },
      disconnected_notice_duration: {
        type: 'integer',
        description: 'how long display disconnection notice in ms; 0 to disable',
        default: 10000,
      },
      disconnected_notice: {
        type: 'string',
        description: 'disable display of disconnected notice',
        default: '',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.el.updateProperties({
      layout: 'single',
      views: 'notifications',
      items: [{
        tag: 'mele-notifications',
        key: 'notifications',
        multi: true,
        class: 'noselect',
        open: this.config.open,
        sort: ['priority#+', 'time#+'],
      }],
    })
    this.elNotifications = this.el.find('@notifications')

    const ctx = require.context('./sounds', false, /\.mp3$/)
    this.sounds = {}
    ctx.keys().forEach(k => {
      const name = k.replace('./', '').replace(/\.mp3$/i, '')
      this.sounds[name] = ctx(k)
    })

    this.elAudio = this.el.appendChildren({ tag: 'audio' })

    if(this.config.disconnected_notice_duration > 0) {
      this.createDisconnectedNotice()
    }

    this.subscribe('#NOTIFICATIONS')
    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Creates notification
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.id - reference id that will be sent back to requesting listener when a button is clicked
  * @param {string} [params.message] - Text of the notification
  * @param {string} [params.icon] - icon of the notification
  * @param {string} [params.color] - color label on the notification
  * @param {number} [params.priority] - sets the order in which notifications are displayed. The higher the number, the higher the order in notification list
  * @param {number} [params.timeout] - sets the timeout after which the notification will disappear from target device
  * @param {string} [params.position] - sets the position of action buttons, options: left, center, right
  * @param {boolean} [params.selected] - Shows if the notification element is preopened
  * @param {string} [params.sound] - alert sound
  * @param {array} [params.actions] - Actions available to respond to notification
  */
  mmNotify(sender, params) {
    let message = _.toString(params.message)
    message = this.el.translate(message)
    const key = this.address2string(sender)+'/'+params.id

    this.playSound(params.sound)

    if(message==='') {
      this.elNotifications.deleteItems(key)
    }else{
      const keys = this.elNotifications.items.map(e=>e.key)
      if(params.timeout && keys.includes(key)) {
        const el = this.elNotifications.getItemElement(key)
        el.label = message
        el.timeoutChanged(el.timeout, el.timeout)
      } else {
        this.elNotifications.updateItems({
          key,
          priority: params.priority,
          iconleft: params.icon,
          label: message,
          color: params.color,
          // time: params.time || parseInt(Date.now()/1000),
          timeout: params.timeout,
          actions: params.actions,
          actionsposition: params.position,
          selected: params.selected,
        })
      }

      if(params.selected) {
        this.elNotifications.selected = key
      }
    }
  } // mmNotify

  async playSound(sound) {
    if(!this.sounds[sound]) return
    if(!this.el.ownerDocument.userInteraction) return
    try{
      this.elAudio.src = this.sounds[sound]
      this.elAudio.play()
    }catch(e) {
      this.warn('could not play audio'+e)
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * mele-notif informs module on its click
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.key - key of the mele-notif
  * @param {string} params.action - notificatin action
  */
  mmNotifyActionClicked(sender, params) {
    const m = params.key.match(/^([^/]+)\/(.+)$/)
    if(!m) return
    this.publish(m[1], 'mmNotifyAction', { id: m[2], action: params.action })
    if(params.action === 'form.close') {
      this.publishAs(m[1], this.listener_id, 'mmNotify', { id: m[2] })
    }
  } // mmNotifyActionClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Ask module to close all notifications
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmNotifyClose(sender, params) {
    this.elNotifications.deleteItems()
  } // mmNotifyClose

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * Used to send playSound to mmodNotifications
 * @param {object} sender - message sender
 * @param {object} params - message parameters
 * @param {string} params.sound - alert sound
 */
  mmSound(sender, params) {
    if(!this.sounds[params.sound]) return
    if(this.config.audio_disabled) return
    this.playSound(params.sound)
  } // mmSound

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    if(this.config.disconnected_notice_duration > 0) {
      this.el.classList.remove('disconnected')
      this.el.classList.remove('disconnected_notice')
      if(this.hideDisconnectedNotice) {
        clearTimeout(this.hideDisconnectedNotice)
        delete this.hideDisconnectedNotice
      }
    }
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} params.url - URL of disconnected server
  */
  mmServerDisconnected(sender, params) {
    if(this.config.disconnected_notice_duration > 0) {
      this.el.classList.add('disconnected')
      this.el.classList.add('disconnected_notice')
      if(this.hideDisconnectedNotice) {
        clearTimeout(this.hideDisconnectedNotice)
      }
      this.hideDisconnectedNotice = setTimeout(()=>{
        this.el.classList.remove('disconnected_notice')
      }, this.config.disconnected_notice_duration)
    }
  } // mmServerDisconnected

  createDisconnectedNotice() {
    // create line and text box used for indicating disconnection from server
    const items = [
      {
        key: 'disconnected_line',
        tag: 'div',
      },
    ]
    const label = String(this.config.disconnected_notice ?? '')
    if(label !== '') {
      items.push({
        key: 'disconnected_notice',
        tag: 'mele-text',
        label: String(this.config.disconnected_notice ?? ''),
      })
    }
    this.el.prependItems(items)
  }
} // mmodNotifications
